import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRENGTH_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "1 uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "1 number", test: (p) => /[0-9]/.test(p) },
  { label: "1 special character", test: (p) => /[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(p) },
];

export const ChangePasswordModal = ({ open, onClose, token, forced = false }) => {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = STRENGTH_RULES.filter((r) => r.test(newPw)).length;
  const strengthPct = (strength / STRENGTH_RULES.length) * 100;
  const strengthColor = strengthPct <= 25 ? "#ec474e" : strengthPct <= 50 ? "#f59e0b" : strengthPct <= 75 ? "#3b82f6" : "#22c55e";
  const passwordsMatch = newPw && confirm && newPw === confirm;
  const canSubmit = current && newPw && confirm && passwordsMatch && strength === STRENGTH_RULES.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: current, new_password: newPw }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Password changed successfully!");
      setCurrent(""); setNewPw(""); setConfirm("");
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={forced ? undefined : () => onClose(false)}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Lock size={18} className="text-[#08263e]" />
            {forced ? "Password Change Required" : "Change Password"}
          </DialogTitle>
        </DialogHeader>
        {forced && (
          <p className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-md" data-testid="forced-change-notice">
            For security, you must change your password before continuing.
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="current-pw">Current Password</Label>
            <div className="relative">
              <Input
                id="current-pw" type={showCurrent ? "text" : "password"} value={current}
                onChange={(e) => setCurrent(e.target.value)} placeholder="Enter current password"
                data-testid="current-password-input"
              />
              <button type="button" className="absolute right-2 top-2.5 text-gray-400" onClick={() => setShowCurrent(!showCurrent)}>
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="new-pw">New Password</Label>
            <div className="relative">
              <Input
                id="new-pw" type={showNew ? "text" : "password"} value={newPw}
                onChange={(e) => setNewPw(e.target.value)} placeholder="Enter new password"
                data-testid="new-password-input"
              />
              <button type="button" className="absolute right-2 top-2.5 text-gray-400" onClick={() => setShowNew(!showNew)}>
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {/* Strength indicator */}
            {newPw && (
              <div className="mt-2 space-y-1.5" data-testid="password-strength">
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${strengthPct}%`, backgroundColor: strengthColor }} />
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {STRENGTH_RULES.map((rule, i) => (
                    <span key={i} className={`text-[10px] flex items-center gap-1 ${rule.test(newPw) ? "text-emerald-600" : "text-gray-400"}`}>
                      <ShieldCheck size={10} /> {rule.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="confirm-pw">Confirm New Password</Label>
            <Input
              id="confirm-pw" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter new password"
              data-testid="confirm-password-input"
            />
            {confirm && !passwordsMatch && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>
          <div className="flex gap-2 justify-end pt-2">
            {!forced && (
              <Button type="button" variant="outline" onClick={() => onClose(false)} data-testid="cancel-change-pw">Cancel</Button>
            )}
            <Button type="submit" disabled={!canSubmit || loading} className="bg-[#08263e] text-white" data-testid="submit-change-pw">
              {loading ? "Changing..." : "Change Password"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
